import { lazy, Suspense } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AppSpinner from "./components/AppSpinner/AppSpinner";
import LoginPage from './LoginPage';
import CreateAccountPage from './CreateAccountPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import EmailVerificationPage from './EmailVerificationPage';
import EulaPage from './EulaPage';
import RequireAuthenticatedLayout from './containers/RequireAuthenticatedLayout';
import RouteErrorBoundary from './containers/RouteErrorBoundary';
import ProductsContainer from './containers/Products/ProductsContainer';
import AddProductContainer from './containers/Products/AddProductContainer';
import EditProductContainer from './containers/Products/EditProductContainer';
import ArchiveProductContainer from './containers/Products/ArchiveProductContainer';
import BrandsContainer from './containers/Brands/BrandsContainer';
import AddBrandContainer from './containers/Brands/AddBrandContainer';
import EditBrandContainer from './containers/Brands/EditBrandContainer';
import OrganizationsContainer from './containers/Organizations/OrganizationsContainer';
import AddOrganizationContainer from './containers/Organizations/AddOrganizationContainer';
import EditOrganizationContainer from './containers/Organizations/EditOrganizationContainer';
import TemplatesContainer from './containers/Templates/TemplatesContainer';
import NewTemplateContainer from './containers/Templates/NewTemplateContainer';
import EditTemplateContainer from './containers/Templates/EditTemplateContainer';
import UsersContainer from './containers/Users/UsersContainer';
import AddUserContainer from './containers/Users/AddUserContainer';
import DenyUserContainer from './containers/Users/DenyUserContainer';
import EditUserContainer from './containers/Users/EditUserContainer';
import ProfileContainer from './containers/Profile/ProfileContainer';
import AdminContainer from './containers/Admin/AdminContainer';
import CoaResultsPage from './CoaResultsPage';
import CoaResultsContainer from './containers/Coa/CoaResultsContainer';
import LandingPageContainer from './containers/Landing/LandingPageContainer';
import PackagesContainer from './containers/Packages/PackagesContainer';
import AddPackageContainer from './containers/Packages/AddPackageContainer';
import EditPackageContainer from './containers/Packages/EditPackageContainer';
import EditStagedPackageContainer from './containers/Packages/EditStagedPackageContainer';
import PackageImportContainer from './containers/Packages/PackageImportContainer';
import PackageImportSelectContainer from './containers/Packages/PackageImportSelectContainer';
import PackagesLayout from './containers/Packages/PackagesLayout';
import StagePackageContainer from './containers/Packages/StagePackageContainer';
import StageTagsContainer from './containers/Packages/StageTagsContainer';
import PrintRunsContainer from './containers/Prints/PrintRunsContainer';
import NewPrintContainer from './containers/Prints/NewPrintContainer';
import NewStagedPrintContainer from './containers/Prints/NewStagedPrintContainer';
import EditPrintContainer from './containers/Prints/EditPrintContainer';
import ImportsContainer from './containers/Imports/ImportsContainer';
import ImportsLayout from './containers/Imports/ImportsLayout';
import AddFacilityContainer from './containers/Imports/AddFacilityContainer';
import EditFacilityContainer from './containers/Imports/EditFacilityContainer';
import ImportJobViewContainer from './containers/Imports/ImportJobViewContainer';
import MetrcAuthContainer from './containers/Imports/MetrcAuthContainer';
import MetrcSsoCallbackContainer from './containers/Imports/MetrcSsoCallbackContainer';
import MetrcSelectContainer from './containers/Imports/MetrcSelectContainer';
import MetrcProductsContainer from './containers/Imports/MetrcProductsContainer';
import MetrcImportRouteGuard from './containers/Imports/MetrcImportRouteGuard';
import InternalPage from './InternalPage';
import OrganizationFacilitiesAndEmailsReportContainer from './containers/Organizations/OrganizationFacilitiesAndEmailsReportContainer';
import PrivateRoute from './PrivateRoute';
import {
  LegacyAppSessionProvider,
  useLegacyAppSession,
} from './contexts/LegacyAppSessionContext';
import { SsoReauthProvider } from './contexts/SsoReauthContext';
import { APP2_SUPPORTED_ROUTE_PREFIXES } from './constants';
import { getPostAuthenticationRoute } from './utils/postAuthenticationRoute';
import { combineResourceActions, getAdminAction, getResourceActions, getWriteActions } from './utils/permissionCatalog';

const PulseContainer = lazy(() => import('./containers/Pulse/PulseContainer'));

export function AppRoutes() {
  const session = useLegacyAppSession();
  const location = useLocation();
  const navigate = useNavigate();
  // Consumer-facing pages must render without a session, so they skip the spinner and EULA gates.
  const isPublicConsumerRoute = location.pathname.startsWith('/landingpage/')
    || location.pathname.startsWith('/coa_results');
  if (!isPublicConsumerRoute && !session.isSessionReady && session.hasStoredToken) {
    return <AppSpinner/>;
  }

  if (!isPublicConsumerRoute && session.eulaGatePending && session.isAuthenticated) {
    return (
      <EulaPage
        onAccept={session.acceptEula}
        onDecline={session.handleLogout}
      />
    );
  }

  const defaultAuthenticatedRoute = APP2_SUPPORTED_ROUTE_PREFIXES.some((prefix) =>
    session.defaultRoute.startsWith(prefix)
  )
    ? session.defaultRoute
    : '/profile';
  const defaultPublicRoute = session.isAuthenticated ? defaultAuthenticatedRoute : '/login';
  const postAuthenticationRoute = getPostAuthenticationRoute(
    location,
    defaultAuthenticatedRoute,
  );
  const routeSearchParams = new URLSearchParams(location.search || '');
  const requestedOrganizationName = String(routeSearchParams.get('organization') || '').trim();
  const isApprovalOrganizationPending =
    routeSearchParams.get('isApprovingUserRequestToJoinOrganization')?.trim() === 'true' &&
    Boolean(requestedOrganizationName) &&
    String(session.org?.name || '').trim().toLowerCase() !== requestedOrganizationName.toLowerCase();
  const productActions = getResourceActions('Products');
  const productWriteActions = getWriteActions('Products');
  const packageActions = getResourceActions('Packages');
  const packageWriteActions = getWriteActions('Packages');
  const templateActions = getResourceActions('Templates');
  const templateWriteActions = getWriteActions('Templates');
  const printActions = getResourceActions('PrintRuns');
  const printWriteActions = getWriteActions('PrintRuns');
  const brandActions = getResourceActions('Brands');
  const brandWriteActions = getWriteActions('Brands');
  const organizationWriteActions = getWriteActions('OrganizationManagement');
  const importActions = getResourceActions('Imports');
  const importWriteActions = getWriteActions('Imports');
  const facilityActions = getResourceActions('Facilities');
  const facilityWriteActions = getWriteActions('Facilities');
  const userManagementActions = getResourceActions('UserManagement');
  const userManagementWriteActions = getWriteActions('UserManagement');

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={(
            session.isAuthenticated
              ? <Navigate to={postAuthenticationRoute} replace />
              : (
                <LoginPage
                  onSignIn={session.handleLogin}
                  onCreateAccount={() => navigate('/signup')}
                  onForgot={() => navigate('/forgot')}
                  error={session.loginError}
                  busy={session.loginBusy}
                />
              )
          )}
        />
        <Route
          path="/signup"
          element={(
            <CreateAccountPage
              onSignIn={() => navigate('/login')}
              onForgot={() => navigate('/forgot')}
              onSubmit={session.handleSignup}
            />
          )}
        />
        <Route
          path="/forgot"
          element={(
            <ForgotPasswordPage
              onSignIn={() => navigate('/login')}
            />
          )}
        />
        <Route path="/reset" element={<ResetPasswordPage />}/>
        <Route path="/verify/email" element={<EmailVerificationPage />}/>
        <Route path="/coa_results" element={<CoaResultsPage activeCoas={[]} />} />
        <Route
          path="/coa_results/:issuanceId/:index?"
          element={(
            <RouteErrorBoundary><CoaResultsContainer /></RouteErrorBoundary>
          )}
        />
        <Route
          path="/landingpage/:issuanceId/:index?"
          element={(
            <RouteErrorBoundary><LandingPageContainer /></RouteErrorBoundary>
          )}
        />

        <Route element={<RequireAuthenticatedLayout standalone />}>
          <Route path="/internal/pulse" element={(
            <PrivateRoute requiredAction="ReadInternalReports">
              <RouteErrorBoundary><Suspense fallback={<div role="status">Loading Pulse…</div>}><PulseContainer /></Suspense></RouteErrorBoundary>
            </PrivateRoute>
          )} />
        </Route>
        <Route element={<RequireAuthenticatedLayout />}>
          <Route
            path="/admin"
            element={(
              <PrivateRoute requiredAction={combineResourceActions(
                'Brands', 'OrganizationManagement', 'Imports', 'Facilities', 'UserManagement')} redirectTo="/profile">
                <RouteErrorBoundary><AdminContainer /></RouteErrorBoundary>
              </PrivateRoute>
            )}
          />
          <Route
            path="/internal/*"
            element={(
              <RouteErrorBoundary>
                <InternalPage organizationId={session.org?.id} />
              </RouteErrorBoundary>
            )}
          />
          <Route path="/packages" element={<PrivateRoute requiredAction={packageActions} redirectTo="/profile"><PackagesLayout /></PrivateRoute>}>
            <Route
              index
              element={(
                <RouteErrorBoundary><PackagesContainer /></RouteErrorBoundary>
              )}
            />
            <Route
              path="new"
              element={(
                <PrivateRoute requiredAction={packageWriteActions} redirectTo="/profile"><RouteErrorBoundary><AddPackageContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
            <Route
              path=":packageRowId/edit"
              element={(
                <PrivateRoute requiredAction={packageWriteActions} redirectTo="/profile"><RouteErrorBoundary><EditPackageContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
            <Route
              path="staged/edit"
              element={(
                <PrivateRoute requiredAction={packageWriteActions} redirectTo="/profile"><RouteErrorBoundary><EditStagedPackageContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
            <Route element={(
              <PrivateRoute requiredAction={packageWriteActions} redirectTo="/profile">
                <PrivateRoute requiredAction={importWriteActions} redirectTo="/profile">
                  <Outlet />
                </PrivateRoute>
              </PrivateRoute>
            )}>
              <Route path="import" element={<RouteErrorBoundary><PackageImportContainer /></RouteErrorBoundary>} />
              <Route path="import/select" element={<RouteErrorBoundary><PackageImportSelectContainer /></RouteErrorBoundary>} />
              <Route path="stage" element={<RouteErrorBoundary><StagePackageContainer /></RouteErrorBoundary>} />
              <Route path="stage/select" element={<RouteErrorBoundary><StageTagsContainer /></RouteErrorBoundary>} />
            </Route>
          </Route>
          <Route path="/products" element={<PrivateRoute requiredAction={productActions} redirectTo="/profile"><Outlet /></PrivateRoute>}>
            <Route
              index
              element={(
                <RouteErrorBoundary><ProductsContainer /></RouteErrorBoundary>
              )}
            />
            <Route
              path="new"
              element={(
                <PrivateRoute requiredAction={productWriteActions} redirectTo="/profile">
                  <RouteErrorBoundary><AddProductContainer /></RouteErrorBoundary>
                </PrivateRoute>
              )}
            />
            <Route
              path=":productId/edit"
              element={(
                <PrivateRoute requiredAction={productWriteActions} redirectTo="/profile">
                  <RouteErrorBoundary><EditProductContainer /></RouteErrorBoundary>
                </PrivateRoute>
              )}
            />
            <Route
              path=":productId/archive"
              element={(
                <PrivateRoute requiredAction={getAdminAction('Products')} redirectTo="/profile">
                  <RouteErrorBoundary><ArchiveProductContainer /></RouteErrorBoundary>
                </PrivateRoute>
              )}
            />
          </Route>

          <Route path="/brands" element={<PrivateRoute requiredAction={brandActions} redirectTo="/profile"><Outlet /></PrivateRoute>}>
            <Route
              index
              element={(
                <RouteErrorBoundary><BrandsContainer /></RouteErrorBoundary>
              )}
            />
            <Route
              path="new"
              element={(
                <PrivateRoute requiredAction={brandWriteActions} redirectTo="/profile"><RouteErrorBoundary><AddBrandContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
            <Route
              path=":brandId/edit"
              element={(
                <PrivateRoute requiredAction={brandWriteActions} redirectTo="/profile"><RouteErrorBoundary><EditBrandContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
          </Route>

          <Route path="/organizations" element={<PrivateRoute><Outlet /></PrivateRoute>}>
            <Route
              index
              element={(
                <RouteErrorBoundary><OrganizationsContainer /></RouteErrorBoundary>
              )}
            />
            <Route
              path="facilities-and-emails-report"
              element={(
                <RouteErrorBoundary>
                  <OrganizationFacilitiesAndEmailsReportContainer />
                </RouteErrorBoundary>
              )}
            />
            <Route
              path="merge"
              element={<Navigate to="/internal/organizations/merge" replace />}
            />
            <Route
              path="new"
              element={(
                <PrivateRoute requiredAction={organizationWriteActions} redirectTo="/profile"><RouteErrorBoundary><AddOrganizationContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
            <Route
              path=":orgId/edit"
              element={(
                <PrivateRoute requiredAction={organizationWriteActions} redirectTo="/profile"><RouteErrorBoundary><EditOrganizationContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
          </Route>

          <Route path="/templates" element={<PrivateRoute requiredAction={templateActions} redirectTo="/profile"><Outlet /></PrivateRoute>}>
            <Route
              index
              element={(
                <RouteErrorBoundary><TemplatesContainer /></RouteErrorBoundary>
              )}
            />
            <Route
              path="new"
              element={(
                <PrivateRoute requiredAction={templateWriteActions} redirectTo="/profile"><RouteErrorBoundary><NewTemplateContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
            <Route
              path=":templateId/design"
              element={(
                <PrivateRoute requiredAction={templateWriteActions} redirectTo="/profile"><RouteErrorBoundary><EditTemplateContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
          </Route>
          <Route path="/prints" element={<PrivateRoute requiredAction={printActions} redirectTo="/profile"><Outlet /></PrivateRoute>}>
            <Route
              index
              element={(
                <RouteErrorBoundary><PrintRunsContainer /></RouteErrorBoundary>
              )}
            />
            <Route
              path="new"
              element={(
                <PrivateRoute requiredAction={printWriteActions} redirectTo="/profile"><RouteErrorBoundary><NewPrintContainer /></RouteErrorBoundary></PrivateRoute>
              )}
            />
            <Route
              path="staged/new"
              element={(<PrivateRoute requiredAction={printWriteActions} redirectTo="/profile"><RouteErrorBoundary><NewStagedPrintContainer /></RouteErrorBoundary></PrivateRoute>)}
            />
            <Route
              path=":printRunId/edit"
              element={(<PrivateRoute requiredAction={printWriteActions} redirectTo="/profile"><RouteErrorBoundary><EditPrintContainer /></RouteErrorBoundary></PrivateRoute>)}
            />
          </Route>

          <Route path="/users" element={<PrivateRoute requiredAction={userManagementActions} redirectTo="/profile"><Outlet /></PrivateRoute>}>
            <Route
              index
              element={(<RouteErrorBoundary><UsersContainer /></RouteErrorBoundary>)}
            />
            <Route
              path="new"
              element={(
                <PrivateRoute
                  requiredAction={userManagementWriteActions}
                  bypassAuthorization={isApprovalOrganizationPending}
                  redirectTo="/profile"
                >
                  <RouteErrorBoundary><AddUserContainer /></RouteErrorBoundary>
                </PrivateRoute>
              )}
            />
            <Route
              path="deny"
              element={(<PrivateRoute requiredAction={userManagementWriteActions} redirectTo="/profile"><RouteErrorBoundary><DenyUserContainer /></RouteErrorBoundary></PrivateRoute>)}
            />
            <Route
              path=":userId/edit"
              element={(
                <PrivateRoute requiredAction={userManagementWriteActions} redirectTo="/profile">
                  <RouteErrorBoundary><EditUserContainer /></RouteErrorBoundary>
                </PrivateRoute>
              )}
            />
          </Route>

          <Route
            path="/profile/*"
            element={(<RouteErrorBoundary><ProfileContainer /></RouteErrorBoundary>)}
          />
          <Route path="/imports" element={<PrivateRoute requiredAction={[...importActions, ...facilityActions]} redirectTo="/profile"><ImportsLayout /></PrivateRoute>}>
            <Route
              index
              element={(<RouteErrorBoundary><ImportsContainer /></RouteErrorBoundary>)}
            />
            <Route
              path="facilities/new"
              element={(<PrivateRoute requiredAction={facilityWriteActions} redirectTo="/profile"><RouteErrorBoundary><AddFacilityContainer /></RouteErrorBoundary></PrivateRoute>)}
            />
            <Route
              path="facilities/:facilityId/edit"
              element={(<PrivateRoute requiredAction={facilityWriteActions} redirectTo="/profile"><RouteErrorBoundary><EditFacilityContainer /></RouteErrorBoundary></PrivateRoute>)}
            />
            <Route
              path="jobs/:jobId"
              element={(<PrivateRoute requiredAction={importActions} redirectTo="/profile"><RouteErrorBoundary><ImportJobViewContainer /></RouteErrorBoundary></PrivateRoute>)}
            />
            <Route
              path="metrc/auth"
              element={(<PrivateRoute requiredAction={importWriteActions} redirectTo="/profile"><MetrcImportRouteGuard><RouteErrorBoundary><MetrcAuthContainer /></RouteErrorBoundary></MetrcImportRouteGuard></PrivateRoute>)}
            />
            <Route
              path="metrc/sso-callback"
              element={(<PrivateRoute requiredAction={importWriteActions} redirectTo="/profile"><MetrcImportRouteGuard><RouteErrorBoundary><MetrcSsoCallbackContainer /></RouteErrorBoundary></MetrcImportRouteGuard></PrivateRoute>)}
            />
            <Route
              path="metrc/select"
              element={(<PrivateRoute requiredAction={importWriteActions} redirectTo="/profile"><MetrcImportRouteGuard><RouteErrorBoundary><MetrcSelectContainer /></RouteErrorBoundary></MetrcImportRouteGuard></PrivateRoute>)}
            />
            <Route
              path="metrc/products"
              element={(<PrivateRoute requiredAction={importWriteActions} redirectTo="/profile"><MetrcImportRouteGuard><RouteErrorBoundary><MetrcProductsContainer /></RouteErrorBoundary></MetrcImportRouteGuard></PrivateRoute>)}
            />
          </Route>
          <Route path="/facilities" element={<Navigate to="/imports?tab=facilities" replace />} />
        </Route>

        <Route path="/" element={<Navigate to={defaultPublicRoute} replace/>}/>
        <Route path="*" element={<Navigate to={defaultPublicRoute} replace/>}/>

      </Routes>
    </>
  );
}

function App() {
  return (
    <LegacyAppSessionProvider>
      <SsoReauthProvider>
        <AppRoutes />
      </SsoReauthProvider>
    </LegacyAppSessionProvider>
  );
}

export default App;
